import { useState } from 'react';
import MobileRecordCard from './MobileRecordCard';
import { formatTRY } from '@/lib/currencyFormat';
import Badge from '@/components/base/Badge';
import { installmentStatus } from '@/lib/statusDefs';

interface CustomerNotesTabProps {
  notes: any[];
  onAddNote: () => void;
  onPayNote: (noteId: string) => void;
  onViewNoteDetail?: (note: any) => void;
}

export default function CustomerNotesTab({ notes, onAddNote, onPayNote, onViewNoteDetail }: CustomerNotesTabProps) {
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedNote, setSelectedNote] = useState<any>(null);

  if (notes.length === 0) {
    return (
      <div className="p-5">
        <div className="flex flex-col items-center py-8 gap-2">
          <div className="w-12 h-12 rounded-2xl bg-background-100 flex items-center justify-center">
            <i className="ri-file-list-3-line text-xl text-foreground-300"></i>
          </div>
          <p className="text-sm font-medium text-foreground-500">Henüz senet kaydı yok</p>
          <button onClick={onAddNote} className="btn-primary text-sm cursor-pointer whitespace-nowrap">
            <i className="ri-add-line mr-1"></i>Senet Ekle
          </button>
        </div>
      </div>
    );
  }

  const handleRowClick = (note: any) => {
    setSelectedNote(note);
    setShowDetailModal(true);
    onViewNoteDetail?.(note);
  };

  return (
    <div className="p-4 lg:p-5">
      {/* Desktop table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full min-w-[1200px]">
          <thead><tr className="border-b border-background-100"><th className="table-header">Senet No</th><th className="table-header">Kaynak</th><th className="table-header">İlk Tutar</th><th className="table-header">Toplam Ödenen</th><th className="table-header">Kalan Borç</th><th className="table-header">Alışveriş Tarihi</th><th className="table-header">Vade Tarihi</th><th className="table-header">Kitap</th><th className="table-header">Sayfa</th><th className="table-header">Sıra</th><th className="table-header">Durum</th><th className="table-header text-center">İşlem</th></tr></thead>
          <tbody>
            {notes.map((note: any) => {
              const remaining = note.remaining_amount !== undefined ? note.remaining_amount : ((note.amount || 0) - (note.paid_amount || 0));
              const isLegacy = note.source_type === 'legacy_opening_balance';
              return (
                <tr
                  key={note.id}
                  className="border-b border-background-100 hover:bg-background-50 cursor-pointer"
                  onClick={() => handleRowClick(note)}
                >
                  <td className="table-cell">
                    <span className="text-primary-600 font-medium">{note.note_no}</span>
                    {isLegacy && note.legacy_note_no && <p className="text-[10px] text-foreground-400">Eski: {note.legacy_note_no}</p>}
                  </td>
                  <td className="table-cell">
                    {isLegacy ? <span className="bg-amber-100 text-amber-700 text-xs px-1.5 py-0.5 rounded-full whitespace-nowrap">Eski Sistem</span> : <span className="bg-secondary-100 text-secondary-700 text-xs px-1.5 py-0.5 rounded-full whitespace-nowrap">Satış</span>}
                  </td>
                  <td className="table-cell font-medium">{formatTRY(note.amount || 0)}</td>
                  <td className="table-cell text-emerald-600 font-medium">{formatTRY(note.paid_amount || 0)}</td>
                  <td className="table-cell">{remaining > 0 ? <span className="text-red-500 font-medium">{formatTRY(remaining)}</span> : <span className="text-emerald-600">—</span>}</td>
                  <td className="table-cell text-foreground-500">{note.purchase_date ? new Date(note.purchase_date).toLocaleDateString('tr-TR') : note.issue_date ? new Date(note.issue_date).toLocaleDateString('tr-TR') : '—'}</td>
                  <td className="table-cell text-foreground-500">{note.due_date ? new Date(note.due_date).toLocaleDateString('tr-TR') : '—'}</td>
                  <td className="table-cell"><span className="bg-secondary-100 text-secondary-800 text-xs px-1.5 py-0.5 rounded-full">{note.note_book_no}</span></td>
                  <td className="table-cell font-medium text-foreground-700">{note.note_page_no}</td>
                  <td className="table-cell font-medium text-foreground-700">{note.note_sequence_no || '—'}</td>
                  <td className="table-cell py-2"><Badge badgeClass={installmentStatus.badgeClass(note.status)} icon={installmentStatus.icon(note.status)}>{note.status}</Badge>{note.enforcement_status && <span className="bg-red-100 text-red-700 text-[10px] px-1.5 py-0.5 rounded-full ml-1 whitespace-nowrap">İcrada</span>}</td>
                  <td className="table-cell py-2">
                    <div className="flex items-center justify-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                      {remaining > 0 && (
                        <button onClick={() => onPayNote(note.id)} className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center cursor-pointer hover:bg-emerald-100 transition-colors" title="Senet Ödemesi Al">
                          <i className="ri-bank-card-line text-emerald-600 text-sm"></i>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="lg:hidden space-y-2">
        {notes.map((note: any) => {
          const remaining = note.remaining_amount !== undefined ? note.remaining_amount : ((note.amount || 0) - (note.paid_amount || 0));
          const isLegacy = note.source_type === 'legacy_opening_balance';
          return (
            <div key={note.id} className="cursor-pointer" onClick={() => handleRowClick(note)}>
              <MobileRecordCard
                title={note.note_no}
                subtitle={isLegacy ? 'Eski Sistem' : 'Satış Kaynaklı'}
                rows={[
                  { label: 'Tutar', value: formatTRY(note.amount || 0) },
                  { label: 'Ödenen', value: formatTRY(note.paid_amount || 0), color: 'emerald' },
                  { label: 'Kalan', value: remaining > 0 ? formatTRY(remaining) : '—', highlight: remaining > 0, color: 'red' },
                  { label: 'Vade', value: note.due_date ? new Date(note.due_date).toLocaleDateString('tr-TR') : '—' },
                  { label: 'Kitap / Sayfa / Sıra', value: `${note.note_book_no || '—'} / ${note.note_page_no || '—'} / ${note.note_sequence_no || '—'}` },
                ]}
                badges={[
                  { text: note.status, className: installmentStatus.badgeClass(note.status), icon: installmentStatus.icon(note.status) },
                  ...(note.enforcement_status ? [{ text: 'İcrada', className: 'bg-red-100 text-red-700 text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap' }] : []),
                ]}
                actionLabel={remaining > 0 ? 'Ödeme Al' : undefined}
                onAction={remaining > 0 ? () => onPayNote(note.id) : undefined}
              />
            </div>
          );
        })}
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedNote && (
        <>
          <div className="fixed inset-0 bg-black/40 z-[60] animate-fade-in" onClick={() => setShowDetailModal(false)} />
          <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 z-[61] max-w-lg mx-auto bg-background-50 rounded-2xl p-6 shadow-xl animate-slide-up max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="text-lg font-semibold text-foreground-900 font-heading">{selectedNote.note_no}</h3>
                <p className="text-xs text-foreground-400 mt-0.5">
                  {selectedNote.source_type === 'legacy_opening_balance' ? 'Eski Sistem' : 'Satış Kaynaklı'}
                </p>
              </div>
              <button onClick={() => setShowDetailModal(false)} className="w-8 h-8 rounded-lg hover:bg-background-100 flex items-center justify-center cursor-pointer flex-shrink-0">
                <i className="ri-close-line text-foreground-500"></i>
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-5">
              <div className="bg-background-100 rounded-lg p-3">
                <p className="text-[11px] text-foreground-400 uppercase tracking-wider">Tutar</p>
                <p className="text-sm font-bold text-foreground-900 mt-1">{formatTRY(selectedNote.amount || 0)}</p>
              </div>
              <div className="bg-background-100 rounded-lg p-3">
                <p className="text-[11px] text-foreground-400 uppercase tracking-wider">Kalan</p>
                <p className={`text-sm font-bold mt-1 ${(selectedNote.remaining_amount || 0) > 0 ? 'text-red-500' : 'text-emerald-600'}`}>
                  {formatTRY(selectedNote.remaining_amount || 0)}
                </p>
              </div>
              <div className="bg-background-100 rounded-lg p-3">
                <p className="text-[11px] text-foreground-400 uppercase tracking-wider">Ödenen</p>
                <p className="text-sm font-bold text-emerald-600 mt-1">{formatTRY(selectedNote.paid_amount || 0)}</p>
              </div>
              <div className="bg-background-100 rounded-lg p-3">
                <p className="text-[11px] text-foreground-400 uppercase tracking-wider">Vade Tarihi</p>
                <p className="text-sm font-bold text-foreground-900 mt-1">
                  {selectedNote.due_date ? new Date(selectedNote.due_date).toLocaleDateString('tr-TR') : '—'}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-5">
              <div className="bg-background-100 rounded-lg p-3 text-center">
                <p className="text-[11px] text-foreground-400 uppercase tracking-wider">Kitap</p>
                <p className="text-sm font-bold text-foreground-900 mt-1">{selectedNote.note_book_no || '—'}</p>
              </div>
              <div className="bg-background-100 rounded-lg p-3 text-center">
                <p className="text-[11px] text-foreground-400 uppercase tracking-wider">Sayfa</p>
                <p className="text-sm font-bold text-foreground-900 mt-1">{selectedNote.note_page_no || '—'}</p>
              </div>
              <div className="bg-background-100 rounded-lg p-3 text-center">
                <p className="text-[11px] text-foreground-400 uppercase tracking-wider">Sıra</p>
                <p className="text-sm font-bold text-foreground-900 mt-1">{selectedNote.note_sequence_no || '—'}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 mb-5">
              <div className="flex-1">
                <p className="text-[11px] text-foreground-400 uppercase tracking-wider mb-1">Durum</p>
                <Badge badgeClass={installmentStatus.badgeClass(selectedNote.status)} icon={installmentStatus.icon(selectedNote.status)}>{selectedNote.status}</Badge>
                {selectedNote.enforcement_status && (
                  <span className="bg-red-100 text-red-700 text-xs px-2 py-0.5 rounded-full ml-2 whitespace-nowrap">İcrada</span>
                )}
              </div>
              {selectedNote.purchase_date && (
                <div className="text-right">
                  <p className="text-[11px] text-foreground-400 uppercase tracking-wider mb-1">Alışveriş Tarihi</p>
                  <p className="text-sm font-medium text-foreground-700">
                    {new Date(selectedNote.purchase_date).toLocaleDateString('tr-TR')}
                  </p>
                </div>
              )}
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setShowDetailModal(false)}
                className="flex-1 btn-outline cursor-pointer"
              >
                Kapat
              </button>
              {(selectedNote.remaining_amount || 0) > 0 && (
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    onPayNote(selectedNote.id);
                  }}
                  className="flex-1 btn-primary cursor-pointer flex items-center justify-center gap-1.5"
                >
                  <i className="ri-bank-card-line"></i>
                  Ödeme Al
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}